import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MonitorWeight as MonitorWeightIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Client } from '../services/clientApi';
import {
  CreateWeightMeasurementRequest,
  WeightMeasurement,
  weightMeasurementApi,
} from '../services/weightMeasurementApi';
import CancelButton from './CancelButton';
import Loading from './Loading';

interface WeightTrackerProps {
  clientId: string;
  clientName: string;
  client?: Client; // BMI hesaplamak için height gerekli
  isDietitian?: boolean; // Diyetisyen mi danışan mı kontrolü
  /** Diyetisyen danışan için ölçüm eklediğinde danışana bildirim gitsin */
  dietitianId?: string;
  dietitianName?: string;
}

const WeightTracker: React.FC<WeightTrackerProps> = ({
  clientId,
  clientName,
  client,
  isDietitian = true, // Varsayılan olarak diyetisyen
  dietitianId,
  dietitianName,
}) => {
  const [measurements, setMeasurements] = useState<WeightMeasurement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMeasurement, setEditingMeasurement] =
    useState<WeightMeasurement | null>(null);

  const [formData, setFormData] = useState<CreateWeightMeasurementRequest>({
    clientId,
    clientName,
    measurementDate: new Date().toISOString().split('T')[0],
    weight: undefined,
    waist: undefined,
    chest: undefined,
    hip: undefined,
    arm: undefined,
    thigh: undefined,
    bodyFatPercentage: undefined,
    muscleMass: undefined,
    notes: '',
  });

  const fetchMeasurements = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await weightMeasurementApi.getMeasurementsByClient(clientId);
      setMeasurements(data);
    } catch (err) {
      setError('Ölçüm verileri yüklenirken hata oluştu');
      console.error('Error fetching measurements:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchMeasurements();
  }, [fetchMeasurements]);

  const handleAddMeasurement = async () => {
    try {
      setLoading(true);
      if (editingMeasurement) {
        await weightMeasurementApi.updateMeasurement(
          editingMeasurement.id,
          formData
        );
      } else {
        const payload = { ...formData };
        if (isDietitian && dietitianId) {
          payload.dietitianId = dietitianId;
          payload.dietitianName = dietitianName ?? undefined;
        }
        await weightMeasurementApi.createMeasurement(payload);
      }
      await fetchMeasurements();
      handleCloseModal();
    } catch (err) {
      setError('Ölçüm kaydedilirken hata oluştu');
      console.error('Error saving measurement:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMeasurement = async (id: string) => {
    if (!window.confirm('Bu ölçümü silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      setLoading(true);
      await weightMeasurementApi.deleteMeasurement(id);
      await fetchMeasurements();
    } catch (err) {
      setError('Ölçüm silinirken hata oluştu');
      console.error('Error deleting measurement:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (measurement?: WeightMeasurement) => {
    if (measurement) {
      setEditingMeasurement(measurement);
      setFormData({
        clientId,
        clientName,
        measurementDate: new Date(measurement.measurementDate)
          .toISOString()
          .split('T')[0],
        weight: measurement.weight,
        waist: measurement.waist,
        chest: measurement.chest,
        hip: measurement.hip,
        arm: measurement.arm,
        thigh: measurement.thigh,
        bodyFatPercentage: measurement.bodyFatPercentage,
        muscleMass: measurement.muscleMass,
        notes: measurement.notes || '',
      });
    } else {
      setEditingMeasurement(null);
      setFormData({
        clientId,
        clientName,
        measurementDate: new Date().toISOString().split('T')[0],
        weight: undefined,
        waist: undefined,
        chest: undefined,
        hip: undefined,
        arm: undefined,
        thigh: undefined,
        bodyFatPercentage: undefined,
        muscleMass: undefined,
        notes: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingMeasurement(null);
  };

  const calculateBMI = (weight: number, height?: number): number | null => {
    if (!height || height <= 0 || !weight || weight <= 0) return null;
    const heightInMeters = height / 100;
    return weight / (heightInMeters * heightInMeters);
  };

  const getBMICategory = (bmi: number): { label: string; color: string } => {
    if (bmi < 18.5) return { label: 'Zayıf', color: '#A5D6A7' };
    if (bmi < 25) return { label: 'Normal', color: '#43A047' };
    if (bmi < 30) return { label: 'Fazla Kilolu', color: '#FF9800' };
    return { label: 'Obez', color: '#F44336' };
  };

  const getLatestMeasurement = () => {
    return measurements.length > 0 ? measurements[0] : null;
  };

  const getPreviousMeasurement = () => {
    return measurements.length > 1 ? measurements[1] : null;
  };

  const calculateChange = (
    current?: number,
    previous?: number
  ): { value: number; isPositive: boolean } | null => {
    if (!current || !previous) return null;
    const change = current - previous;
    return {
      value: Math.abs(change),
      isPositive: change < 0, // Kilo/ölçü azalması pozitif (iyi)
    };
  };

  const latest = getLatestMeasurement();
  const previous = getPreviousMeasurement();
  const weightChange =
    latest?.weight && previous?.weight
      ? calculateChange(latest.weight, previous.weight)
      : null;

  const bmi =
    latest?.weight && client?.height
      ? calculateBMI(latest.weight, client.height)
      : null;

  return loading && measurements.length === 0 ? (
    <Loading
      size="large"
      variant="wave"
      mode="inline"
      message="Kilo takibi verileri yükleniyor..."
    />
  ) : (
    <Box>
      {error && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: '#ffebee' }}>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </Paper>
      )}

      {/* Özet Kartları */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 2,
          mb: 3,
        }}
      >
        <Card elevation={2}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <MonitorWeightIcon sx={{ color: '#43A047' }} />
              <Typography variant="body2" sx={{ color: '#607D8B' }}>
                Son Kilo
              </Typography>
            </Box>
            {latest?.weight ? (
              <>
                <Typography
                  variant="h4"
                  fontWeight="bold"
                  sx={{ color: '#2E7D32' }}
                >
                  {latest.weight.toFixed(1)} kg
                </Typography>
                {weightChange && (
                  <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                    {weightChange.isPositive ? (
                      <TrendingDownIcon
                        sx={{ color: '#43A047', fontSize: 18 }}
                      />
                    ) : (
                      <TrendingUpIcon sx={{ color: '#F44336', fontSize: 18 }} />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        color: weightChange.isPositive ? '#43A047' : '#F44336',
                      }}
                    >
                      {weightChange.isPositive ? '-' : '+'}
                      {weightChange.value.toFixed(1)} kg
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Henüz kayıt yok
              </Typography>
            )}
          </CardContent>
        </Card>

        {bmi && (
          <Card elevation={2}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                BMI
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {bmi.toFixed(1)}
              </Typography>
              <Chip
                label={getBMICategory(bmi).label}
                size="small"
                sx={{
                  mt: 1,
                  backgroundColor: getBMICategory(bmi).color,
                  color: 'white',
                  fontWeight: 'bold',
                }}
              />
            </CardContent>
          </Card>
        )}

        {latest?.waist && (
          <Card elevation={2}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Bel Çevresi
              </Typography>
              <Typography
                variant="h4"
                sx={{ color: '#9C27B0' }}
                fontWeight="bold"
              >
                {latest.waist.toFixed(1)} cm
              </Typography>
            </CardContent>
          </Card>
        )}

        {latest?.bodyFatPercentage && (
          <Card elevation={2}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Vücut Yağ %
              </Typography>
              <Typography
                variant="h4"
                sx={{ color: '#FF9800' }}
                fontWeight="bold"
              >
                {latest.bodyFatPercentage.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Grafik Alanı */}
      {measurements.filter((m) => m.weight).length > 0 && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Kilo İlerlemesi
          </Typography>
          <Box sx={{ height: 300, mt: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={measurements
                  .filter((m) => m.weight)
                  .slice()
                  .reverse()
                  .slice(0, 30)
                  .map((m) => ({
                    date: new Date(m.measurementDate).toLocaleDateString(
                      'tr-TR',
                      {
                        day: '2-digit',
                        month: '2-digit',
                      }
                    ),
                    kilo: m.weight,
                    fullDate: new Date(m.measurementDate).toLocaleDateString(
                      'tr-TR'
                    ),
                  }))}
                margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                  dataKey="date"
                  stroke="#666"
                  style={{ fontSize: '12px' }}
                />
                <YAxis
                  stroke="#666"
                  style={{ fontSize: '12px' }}
                  label={{
                    value: 'Kilo (kg)',
                    angle: -90,
                    position: 'insideLeft',
                    style: { textAnchor: 'middle', fontSize: '12px' },
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                  }}
                  formatter={(value: any) => [`${value} kg`, 'Kilo']}
                  labelFormatter={(label: string) => `Tarih: ${label}`}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="kilo"
                  stroke="#4CAF50"
                  strokeWidth={3}
                  dot={{ fill: '#4CAF50', r: 5 }}
                  activeDot={{ r: 7 }}
                  name="Kilo (kg)"
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      )}

      {/* Vücut Ölçüleri Grafiği */}
      {measurements.filter(
        (m) => m.waist || m.chest || m.hip || m.arm || m.thigh
      ).length > 0 && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Vücut Ölçüleri İlerlemesi
          </Typography>
          <Box sx={{ height: 300, mt: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={measurements
                  .slice()
                  .reverse()
                  .slice(0, 30)
                  .map((m) => ({
                    date: new Date(m.measurementDate).toLocaleDateString(
                      'tr-TR',
                      {
                        day: '2-digit',
                        month: '2-digit',
                      }
                    ),
                    bel: m.waist,
                    göğüs: m.chest,
                    kalça: m.hip,
                    kol: m.arm,
                    bacak: m.thigh,
                    fullDate: new Date(m.measurementDate).toLocaleDateString(
                      'tr-TR'
                    ),
                  }))}
                margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                  dataKey="date"
                  stroke="#666"
                  style={{ fontSize: '12px' }}
                />
                <YAxis
                  stroke="#666"
                  style={{ fontSize: '12px' }}
                  label={{
                    value: 'Ölçü (cm)',
                    angle: -90,
                    position: 'insideLeft',
                    style: { textAnchor: 'middle', fontSize: '12px' },
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                  }}
                  formatter={(value: any) =>
                    value ? [`${value} cm`, ''] : null
                  }
                  labelFormatter={(label: string) => `Tarih: ${label}`}
                />
                <Legend />
                {measurements.some((m) => m.waist) && (
                  <Line
                    type="monotone"
                    dataKey="bel"
                    stroke="#9C27B0"
                    strokeWidth={2}
                    dot={{ fill: '#9C27B0', r: 4 }}
                    name="Bel (cm)"
                    connectNulls
                  />
                )}
                {measurements.some((m) => m.chest) && (
                  <Line
                    type="monotone"
                    dataKey="göğüs"
                    stroke="#A5D6A7"
                    strokeWidth={2}
                    dot={{ fill: '#A5D6A7', r: 4 }}
                    name="Göğüs (cm)"
                    connectNulls
                  />
                )}
                {measurements.some((m) => m.hip) && (
                  <Line
                    type="monotone"
                    dataKey="kalça"
                    stroke="#FF9800"
                    strokeWidth={2}
                    dot={{ fill: '#FF9800', r: 4 }}
                    name="Kalça (cm)"
                    connectNulls
                  />
                )}
                {measurements.some((m) => m.arm) && (
                  <Line
                    type="monotone"
                    dataKey="kol"
                    stroke="#F44336"
                    strokeWidth={2}
                    dot={{ fill: '#F44336', r: 4 }}
                    name="Kol (cm)"
                    connectNulls
                  />
                )}
                {measurements.some((m) => m.thigh) && (
                  <Line
                    type="monotone"
                    dataKey="bacak"
                    stroke="#607D8B"
                    strokeWidth={2}
                    dot={{ fill: '#607D8B', r: 4 }}
                    name="Bacak (cm)"
                    connectNulls
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      )}

      {/* Ölçümler Listesi */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" fontWeight="bold">
            Ölçüm Geçmişi
          </Typography>
          {isDietitian && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenModal()}
              sx={{
                bgcolor: '#43A047',
                '&:hover': {
                  bgcolor: '#388E3C',
                },
                maxWidth: '200px',
              }}
            >
              Ölçüm Ekle
            </Button>
          )}
        </Box>

        {measurements.length > 0 ? (
          <Box>
            {measurements.map((measurement) => (
              <Card
                key={measurement.id}
                sx={{
                  mb: 2,
                  borderLeft: '4px solid #4CAF50',
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: 3,
                  },
                }}
              >
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        gutterBottom
                      >
                        {new Date(
                          measurement.measurementDate
                        ).toLocaleDateString('tr-TR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </Typography>

                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: 'repeat(2, 1fr)',
                            sm: 'repeat(3, 1fr)',
                            md: 'repeat(4, 1fr)',
                          },
                          gap: 2,
                          mt: 2,
                        }}
                      >
                        {measurement.weight && (
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              Kilo
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {measurement.weight.toFixed(1)} kg
                            </Typography>
                          </Box>
                        )}
                        {measurement.waist && (
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              Bel
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {measurement.waist.toFixed(1)} cm
                            </Typography>
                          </Box>
                        )}
                        {measurement.chest && (
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              Göğüs
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {measurement.chest.toFixed(1)} cm
                            </Typography>
                          </Box>
                        )}
                        {measurement.hip && (
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              Kalça
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {measurement.hip.toFixed(1)} cm
                            </Typography>
                          </Box>
                        )}
                        {measurement.arm && (
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              Kol
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {measurement.arm.toFixed(1)} cm
                            </Typography>
                          </Box>
                        )}
                        {measurement.thigh && (
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              Bacak
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {measurement.thigh.toFixed(1)} cm
                            </Typography>
                          </Box>
                        )}
                        {measurement.bodyFatPercentage && (
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              Vücut Yağ %
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {measurement.bodyFatPercentage.toFixed(1)}%
                            </Typography>
                          </Box>
                        )}
                        {measurement.muscleMass && (
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              Kas Kütlesi
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {measurement.muscleMass.toFixed(1)} kg
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {measurement.notes && (
                        <Box
                          sx={{
                            mt: 2,
                            p: 1,
                            backgroundColor: '#f5f5f5',
                            borderRadius: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontStyle: 'italic',
                              color: 'text.secondary',
                            }}
                          >
                            {measurement.notes}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                      {isDietitian && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenModal(measurement)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDeleteMeasurement(measurement.id)
                            }
                            color="error"
                            sx={{
                              '&:hover': {
                                backgroundColor: 'error.light',
                                color: 'white',
                              },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          <Box
            textAlign="center"
            py={4}
            onClick={isDietitian ? () => handleOpenModal() : undefined}
            sx={{
              border: '2px dashed #e0e0e0',
              borderRadius: 2,
              cursor: isDietitian ? 'pointer' : 'default',
              transition: 'all 0.3s ease',
              ...(isDietitian && {
                '&:hover': {
                  borderColor: '#43A047',
                  bgcolor: 'rgba(67, 160, 71, 0.05)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                },
              }),
            }}
          >
            <MonitorWeightIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 2 }} />
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Henüz ölçüm kaydı yok
            </Typography>
            {isDietitian && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, fontStyle: 'italic' }}
              >
                Ölçüm eklemek için tıklayın
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      {/* Ölçüm Ekleme/Düzenleme Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5">
            {editingMeasurement ? 'Ölçüm Düzenle' : 'Yeni Ölçüm Ekle'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Ölçüm Tarihi"
              type="date"
              value={formData.measurementDate}
              onChange={(e) =>
                setFormData({ ...formData, measurementDate: e.target.value })
              }
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 3 }}
            />

            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Kilo ve Vücut Ölçüleri
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 2,
                mb: 3,
              }}
            >
              <TextField
                fullWidth
                label="Kilo (kg)"
                type="number"
                value={formData.weight || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    weight: e.target.value
                      ? parseFloat(e.target.value)
                      : undefined,
                  })
                }
                inputProps={{ min: 0, step: 0.1 }}
              />

              <TextField
                fullWidth
                label="Bel Çevresi (cm)"
                type="number"
                value={formData.waist || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    waist: e.target.value
                      ? parseFloat(e.target.value)
                      : undefined,
                  })
                }
                inputProps={{ min: 0, step: 0.1 }}
              />

              <TextField
                fullWidth
                label="Göğüs (cm)"
                type="number"
                value={formData.chest || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    chest: e.target.value
                      ? parseFloat(e.target.value)
                      : undefined,
                  })
                }
                inputProps={{ min: 0, step: 0.1 }}
              />

              <TextField
                fullWidth
                label="Kalça (cm)"
                type="number"
                value={formData.hip || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    hip: e.target.value
                      ? parseFloat(e.target.value)
                      : undefined,
                  })
                }
                inputProps={{ min: 0, step: 0.1 }}
              />

              <TextField
                fullWidth
                label="Kol (cm)"
                type="number"
                value={formData.arm || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    arm: e.target.value
                      ? parseFloat(e.target.value)
                      : undefined,
                  })
                }
                inputProps={{ min: 0, step: 0.1 }}
              />

              <TextField
                fullWidth
                label="Bacak (cm)"
                type="number"
                value={formData.thigh || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    thigh: e.target.value
                      ? parseFloat(e.target.value)
                      : undefined,
                  })
                }
                inputProps={{ min: 0, step: 0.1 }}
              />
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Vücut Kompozisyonu
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 2,
                mb: 3,
              }}
            >
              <TextField
                fullWidth
                label="Vücut Yağ Yüzdesi (%)"
                type="number"
                value={formData.bodyFatPercentage || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    bodyFatPercentage: e.target.value
                      ? parseFloat(e.target.value)
                      : undefined,
                  })
                }
                inputProps={{ min: 0, max: 100, step: 0.1 }}
              />

              <TextField
                fullWidth
                label="Kas Kütlesi (kg)"
                type="number"
                value={formData.muscleMass || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    muscleMass: e.target.value
                      ? parseFloat(e.target.value)
                      : undefined,
                  })
                }
                inputProps={{ min: 0, step: 0.1 }}
              />
            </Box>

            <TextField
              fullWidth
              label="Notlar"
              value={formData.notes || ''}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              multiline
              rows={3}
              placeholder="Ölçüm hakkında notlar..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <CancelButton onClick={handleCloseModal} />
          <Button
            onClick={handleAddMeasurement}
            variant="contained"
            disabled={loading}
            sx={{
              bgcolor: '#43A047',
              '&:hover': {
                bgcolor: '#388E3C',
              },
            }}
          >
            {editingMeasurement ? 'Güncelle' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WeightTracker;
